'use client';

import { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { FaUser, FaSignOutAlt, FaUserCircle, FaBars } from 'react-icons/fa'
import { FiShield, FiCalendar, FiShoppingBag } from 'react-icons/fi'
import { useTheme } from 'next-themes'
import { useClickOutside } from '@/hooks/useClickOutside'
import { useTranslation } from '@/hooks/useTranslation'
import { useAuth } from '@/hooks/use-auth'
import { toast } from '@/hooks/use-toast';
import CategoryNav from './CategoryNav'
import SearchBar from '../common/SearchBar'
import { useUserProfile } from '@/hooks/use-user'
import { useHeaderConfig } from '@/context/HeaderContext'

const Header = () => {
  const { theme } = useTheme()
  const { t, lang } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false)
  const [isMobileAuthMenuOpen, setIsMobileAuthMenuOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const mobileUserMenuRef = useRef<HTMLDivElement>(null)
  const mobileAuthMenuRef = useRef<HTMLDivElement>(null)

  const { userProfile, logout } = useUserProfile()
  const { hideFullHeaderOnScroll, isScrolled, setIsScrolled, hideMobileHeader, isStaticOnMobile } = useHeaderConfig()

  // Avoid hydration mismatch by only rendering theme-dependent content after mounting
  useEffect(() => {
    setMounted(true)
  }, [])

  // Handle scroll to hide/show category bar on mobile
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      setIsScrolled(scrollPosition > 5)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useClickOutside(menuRef, () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  });

  useClickOutside(userMenuRef, () => {
    if (isUserMenuOpen) {
      setIsUserMenuOpen(false);
    }
  });

  useClickOutside(mobileUserMenuRef, () => {
    if (isMobileUserMenuOpen) {
      setIsMobileUserMenuOpen(false);
    }
  });

  useClickOutside(mobileAuthMenuRef, () => {
    if (isMobileAuthMenuOpen) {
      setIsMobileAuthMenuOpen(false);
    }
  });

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    setIsMobileUserMenuOpen(false);
    try {
      const success = await logout();
      if (!success) {
        toast({
          title: t('auth.toast.error_title'),
          description: t('auth.toast.logout_error'),
          variant: "destructive",
        })
      }
      window.location.href = '/';
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Still redirect, since we've already cleared the local tokens
      window.location.href = '/';
    }
  };

  return (
    <header 
      className={
        isStaticOnMobile 
          ? 'w-full bg-background border-b border-border dark:border-border/70 sm:sticky sm:top-0 sm:z-50'
          : `w-full bg-background border-b border-border dark:border-border/70 ${hideFullHeaderOnScroll ? 'fixed sm:sticky' : 'sticky'} top-0 z-50 transition-all duration-300 ease-in-out ${
              hideMobileHeader 
                ? 'max-h-0 sm:max-h-[500px] overflow-hidden'
                : hideFullHeaderOnScroll && isScrolled 
                  ? 'sm:max-h-none max-h-0 overflow-hidden' 
                  : 'max-h-[500px] overflow-visible'
            }`
      }
    >
      <div className="container-custom">
        <div className="flex h-[60px] sm:h-[90px] items-center justify-between">
          {/* Logo */}
          <Link href={`/${lang}`} prefetch={true} className="flex items-center">
            <Image
              src={mounted && theme === 'dark' ? "/images/logo-dark.png" : "/images/logo.png"}
              alt="Logo"
              width={120}
              height={30}
              style={{ width: 'auto', height: 30 }}
              priority
            />
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden lg:flex flex-1 max-w-xl mx-6">
            <SearchBar
              placeholder={t('header.searchPlaceholder')}
              className="w-full"
              lang={lang}
            />
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-2 sm:space-x-5">
            {/* Mobile: Calendario y Productos detrás del ícono de hamburguesa */}
            <div className="sm:hidden relative" ref={menuRef}>
              <button
                className="flex items-center text-[14px] text-foreground font-medium transition-colors focus:outline-none"
                aria-label={String(t('nav.menu'))}
                aria-expanded={isMenuOpen}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <FaBars className="h-4 w-4" />
              </button>

              {isMenuOpen && (
                <div className="dropdown-menu shadow-lg dark:border-border/70 dark:bg-background dark:shadow-gray-900/30">
                  <Link
                    href={`/${lang}/allEvents`}
                    className="dropdown-item flex items-center hover:text-primary transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FiCalendar className="mr-2 h-4 w-4" />
                    {t('header.calendar')}
                  </Link>
                  <Link
                    href={`/${lang}/products`}
                    className="dropdown-item flex items-center hover:text-primary transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FiShoppingBag className="mr-2 h-4 w-4" />
                    {t('header.products')}
                  </Link>
                </div>
              )}
            </div>

            {/* Desktop: Botones separados de Organizador y Atleta/Perfil */}
            <div className="hidden sm:flex items-center space-x-5">
              {/* Calendario de eventos */}
              <Link
                href={`/${lang}/allEvents`}
                prefetch={true}
                className="flex items-center text-[14px] text-foreground font-medium transition-colors"
                aria-label={String(t('header.calendar'))}
              >
                <FiCalendar className="mr-1.5 h-4 w-4" />
                <span>{t('header.calendar')}</span>
              </Link>

              {/* Productos (afiliación) */}
              <Link
                href={`/${lang}/products`}
                prefetch={true}
                className="flex items-center text-[14px] text-foreground font-medium transition-colors"
                aria-label={String(t('header.products'))}
              >
                <FiShoppingBag className="mr-1.5 h-4 w-4" />
                <span>{t('header.products')}</span>
              </Link>

              {/* Organizer Button */}
              <a
                href={`https://app.rockthesport.com/${lang}/${lang}/security/signin`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-[14px] text-foreground font-medium transition-colors"
                aria-label={String(t('header.organizer'))}
              >
                <FiShield className="mr-1.5 h-4 w-4" />
                <span>{t('header.organizer')}</span>
              </a>

              {userProfile ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    className="flex items-center text-[14px] text-foreground font-medium transition-colors focus:outline-none"
                    aria-label={String(t('nav.profile'))}
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <div className='flex flex-row items-center gap-2'>
                      {
                        userProfile?.profilePicture ? (
                          <Image
                            src={userProfile?.profilePicture || '/images/user.png'}
                            alt="User"
                            width={40}
                            height={40}
                            className="rounded-full"
                            unoptimized
                          />
                        ) : (
                          <div className='bg-accent rounded-full flex items-center justify-center' style={{
                            width: 40,
                            height: 40,
                          }}>
                            <p className='text-white text-xl font-medium'>{userProfile?.firstName.charAt(0)}</p>
                          </div>
                        )
                      }
                      <span>{userProfile?.firstName}</span>
                    </div>
                  </button>

                  {isUserMenuOpen && (
                    <div className="dropdown-menu shadow-lg dark:border-border/70 dark:bg-background dark:shadow-gray-900/30">
                      <Link
                        href={`/${lang}/dashboard/home`}
                        className="dropdown-item flex items-center hover:text-primary transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <FaUserCircle className="mr-2 h-4 w-4" />
                        {t('header.my_account')}
                      </Link>
                      <button
                        className="dropdown-item w-full text-left flex items-center hover:text-primary transition-colors"
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt className="mr-2 h-4 w-4" />
                        {String(t('nav.logout'))}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href={`/${lang}/login`}
                  className="flex items-center text-[14px] text-foreground font-medium transition-colors"
                  aria-label={String(t('nav.profile'))}
                >
                  <FaUser className="mr-1.5 h-4 w-4" />
                  <span>{t('header.account')}</span>
                </Link>
              )}
            </div>

            {/* Mobile: Un solo ícono con menú flotante */}
            {!userProfile && (
              <div className="sm:hidden relative" ref={mobileAuthMenuRef}>
                <button
                  className="flex items-center text-[14px] text-foreground font-medium transition-colors focus:outline-none"
                  aria-label="Acceso"
                  onClick={() => setIsMobileAuthMenuOpen(!isMobileAuthMenuOpen)}
                >
                  <FaUser className="h-4 w-4" />
                </button>

                {isMobileAuthMenuOpen && (
                  <div className="dropdown-menu shadow-lg dark:border-border/70 dark:bg-background dark:shadow-gray-900/30">
                    <Link
                      href={`/${lang}/login`}
                      className="dropdown-item flex items-center hover:text-primary transition-colors"
                      onClick={() => setIsMobileAuthMenuOpen(false)}
                    >
                      {t('header.account')}
                    </Link>
                    <a
                      href={`https://app.rockthesport.com/${lang}/${lang}/security/signin`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dropdown-item flex items-center hover:text-primary transition-colors"
                      onClick={() => setIsMobileAuthMenuOpen(false)}
                    >
                      {t('header.organizer')}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Mobile: User Profile cuando está logueado */}
            {userProfile && (
              <div className="sm:hidden relative" ref={mobileUserMenuRef}>
                <button
                  className="flex items-center text-[14px] text-foreground font-medium transition-colors focus:outline-none"
                  aria-label={String(t('nav.profile'))}
                  onClick={() => setIsMobileUserMenuOpen(!isMobileUserMenuOpen)}
                >
                  {
                    userProfile?.profilePicture ? (
                      <Image
                        src={userProfile?.profilePicture || '/images/user.png'}
                        alt="User"
                        width={32}
                        height={32}
                        className="rounded-full"
                        unoptimized
                      />
                    ) : (
                      <div className='bg-accent rounded-full flex items-center justify-center' style={{
                        width: 32,
                        height: 32,
                      }}>
                        <p className='text-white text-sm font-medium'>{userProfile?.firstName.charAt(0)}</p>
                      </div>
                    )
                  }
                </button>

                {isMobileUserMenuOpen && (
                  <div className="dropdown-menu shadow-lg dark:border-border/70 dark:bg-background dark:shadow-gray-900/30">
                    <Link
                      href={`/${lang}/dashboard/home`}
                      className="dropdown-item flex items-center hover:text-primary transition-colors"
                      onClick={() => setIsMobileUserMenuOpen(false)}
                    >
                      <FaUserCircle className="mr-2 h-4 w-4" />
                      {t('header.my_account')}
                    </Link>
                    <button
                      className="dropdown-item w-full text-left flex items-center hover:text-primary transition-colors"
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt className="mr-2 h-4 w-4" />
                      {String(t('nav.logout'))}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/*   <Link
              href={`/${lang}/cart`}
              className="flex items-center text-[14px] text-foreground font-medium hover:text-primary transition-colors"
              aria-label={String(t('nav.cart'))}
            >
              <FaShoppingCart className="mr-1.5 h-4 w-4" />
              <span className="hidden sm:block">{t('header.cart')}</span>
              <svg className="ml-1 h-4 w-4 hidden sm:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </Link> */}

          </div>
        </div>

        <div className="lg:hidden mb-3">
          <SearchBar
            placeholder={t('header.searchPlaceholder')}
            className="w-full"
            lang={lang}
          />
        </div>

        <div 
          className={
            isStaticOnMobile
              ? 'border-t border-border/30 dark:border-border/50'
              : `border-t border-border/30 dark:border-border/50 transition-all duration-300 ease-in-out overflow-visible sm:max-h-none ${
                  !hideFullHeaderOnScroll && isScrolled 
                    ? 'max-h-0 opacity-0 sm:opacity-100' 
                    : 'max-h-[200px] opacity-100'
                }`
          }
        >
          <CategoryNav />
        </div>
      </div>
    </header>
  )
}

export default Header 